// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { useTranslate } from "helpers";
import {
  deleteFleetDocumentAPI,
  getFleetDocumentsAPI,
} from "services/api/fleet/vehicles/documents/api";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { initialFleetVehicleDocumentSearchForm } from "services/api/fleet/vehicles/documents/consts";
import { FleetDocumentRow, FleetDocumentSearchFormValues } from "services/api/fleet/vehicles/documents/types";
import {
  VehicleDocumentSearchPageCmp,
  VehicleDocumentSearchPageProps,
} from "./types";
import {
  FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_DOCUMENT_EDIT_PAGE_TITLE,
  FLEET_VEHICLE_DOCUMENT_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";

// Lokalne komponente
import VehicleDocumentSearchResults from "./components/VehicleDocumentSearchResults/VehicleDocumentSearchResults";
import VehicleDocumentSearchFilters from "./components/VehicleDocumentSearchFilters/VehicleDocumentSearchFilters";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import VehicleDocumentEditCard from "../VehicleDocumentEditCard/VehicleDocumentEditCard";
import VehicleDocumentAddCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDocumentAddCard/VehicleDocumentAddCard";
import { Dialog } from "ui/components/Dialog";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { Errors } from "services/forms/validation/types";

const VehicleDocumentSearchPage: VehicleDocumentSearchPageCmp = (
  props: VehicleDocumentSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetDocumentRow>
  >(initialDataWithPagination);

  // Filtri se pamte ovdje, pa ih zadrže i paginacija i osvježavanje nakon unosa.
  const [filterValues, setFilterValues] = useState<FleetDocumentSearchFormValues>(
    initialFleetVehicleDocumentSearchForm
  );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [documentId, setDocumentId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});


  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);



  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const fetchSearchResult = async (
    id: string,
    pagination: Pagination,
    values: FleetDocumentSearchFormValues = filterValues
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetDocumentsAPI(
        pagination,
        values.search,
        id,
        "Vehicle",
        values.documentTypeId ?? undefined,
        values.createdFrom,
        values.createdTo
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDocumentId(id);
    setShowAddNewModal(true);
  }, []);

  // Novi dokumenti se unose kao korak "Dokumenti" u čarobnjaku vozila, postojeći se uređuje u edit cardu.
  const handleAddNew = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  // Dokumenti se spremaju odmah pri dodavanju, pa se na zatvaranju osvježava popis.
  const handleUploadModalClose = useCallback(() => {
    setShowUploadModal(false);
    if (id) fetchSearchResult(id, searchResults.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchResults.pagination]);


  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);

    setDocumentId("");
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback(
    (values: FleetDocumentSearchFormValues) => {
      setFilterValues(values);
      if (id) fetchSearchResult(id, searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, searchResults.pagination]
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setDocumentId(id);
    setOpenDialog(true);
  }

  const handleDocumentDeleteDialogConfirm = useCallback(async () => {
    if (!documentId) return;

    try {
      const deleted = await deleteFleetDocumentAPI(documentId, t);

      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setDocumentId("");
    } catch (err) {
      console.error("Greška prilikom brisanja dokumenta:", err);
    }
  }, [documentId, t]);

  const handleDocumentDeleteDialogClose = useCallback(() => {
    setDocumentId("");
    setOpenDialog(false);
  }, []);

  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_DOCUMENT_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_DOCUMENT_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_DOCUMENT_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusChange}
          >
            <Button
              variant="primary"
              children={t("admin.vehicle.document.search.add")}
              startIcon="/media/icons/plus.svg"
              onClick={handleAddNew}
            />
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <VehicleDocumentSearchFilters onSearchChange={handleSearchChange} />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite dokument klikom na 'Dodaj dokument'" />
          ) : (
            <VehicleDocumentSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.document.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.document.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.document.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.document.dialog.delete.canceltext"
        )}
        onConfirm={handleDocumentDeleteDialogConfirm}
        onCancel={handleDocumentDeleteDialogClose}
      />
      {id && (
        <VehicleDocumentAddCard
          show={showUploadModal}
          vehicleId={id}
          onClose={handleUploadModalClose}
        />
      )}
      <Modal
        show={showAddNewModal}
        onHide={() => setShowAddNewModal(false)}
        position="centered"
        size="small"
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={
            documentId
              ? FLEET_VEHICLE_DOCUMENT_EDIT_PAGE_TITLE.text
              : FLEET_VEHICLE_DOCUMENT_CREATE_PAGE_TITLE.text
          }
          onHide={() => setShowAddNewModal(false)}
        />
        <ModalBody>
          {showAddNewModal && (
            <VehicleDocumentEditCard
              vehicleId={id}
              documentId={documentId}
              onSuccess={handleEditSuccess}
              onClose={() => setShowAddNewModal(false)}
            />)}
        </ModalBody>
      </Modal>
    </>
  );
};

VehicleDocumentSearchPage.route =
  FleetVehicleRoutes.FleetVehicleDocumentSearchPageRouter;

export default VehicleDocumentSearchPage;
